import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Home = () => {
  const services = [
    {
      id: 1,
      title: 'AHPRA-Compliant Social Media Management',
      description: 'Consistent, professional content that builds trust with referred patients before they call your rooms. No cookie-cutter templates. No generic medical stock photos. Just content that sounds like you and converts.',
      image: 'https://primary.jwwb.nl/pexels/69/6913295.jpeg?enable-io=true&enable=upscale&crop=0.8333%3A1&width=800',
      link: '/services'
    },
    {
      id: 2,
      title: 'AI-Powered Content Creation',
      description: 'Using AI tools combined with 11 years of social media experience, I create content that stays 100% AHPRA compliant while saving you hours each week. Content that converts GP referrals and Google searches into booked patients.',
      image: 'https://primary.jwwb.nl/pexels/43/4348404.jpeg?enable-io=true&enable=upscale&crop=0.8333%3A1&width=800',
      link: '/services'
    },
    {
      id: 3,
      title: 'Online Presence Audit',
      description: "In 20 minutes, I'll review your current online presence, tell you exactly what's working and what's losing you referred patients, and give you one actionable change you could make today.",
      image: 'https://primary.jwwb.nl/pexels/53/533446.jpeg?enable-io=true&enable=upscale&crop=0.8333%3A1&width=800',
      link: 'https://calendly.com/ainsleygreenslade/onlinepresenceaudit',
      external: true
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
            Most private obstetricians are exceptional at what they do.
          </h1>
          <div className="space-y-4 text-gray-600 leading-relaxed">
            <p>
              But keeping up with social media, staying visible online, and making sure referred patients actually feel confident enough to book?
            </p>
            <p className="font-medium">
              That's a whole other job.
            </p>
            <p>
              One you didn't train for. One you don't have time for. And honestly, one you shouldn't be doing yourself.
            </p>
            <p className="font-semibold text-gray-800 text-lg mt-6">
              That's where I come in.
            </p>
            <p>
              I help private obstetricians and gynaecologists show up consistently online without it eating into the time you need for patients, theatre, and actually having a life.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" style={{ color: '#D4AF37' }}>
            How I Can Help
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Specialized social media and online presence management for private obstetricians who want to convert referrals without the overwhelm.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="aspect-[5/6] overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  {service.external ? (
                    <a href={service.link} target="_blank" rel="noopener noreferrer">
                      <Button 
                        className="bg-[#F0C419] hover:bg-[#D4AF37] text-black font-medium px-6 py-2 rounded transition-colors duration-200"
                      >
                        Book Free Audit
                      </Button>
                    </a>
                  ) : (
                    <Link to={service.link}>
                      <Button 
                        className="bg-[#F0C419] hover:bg-[#D4AF37] text-black font-medium px-6 py-2 rounded transition-colors duration-200"
                      >
                        Learn More
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
            Here's what makes me different from every other agency that's probably already slid into your LinkedIn DMs.
          </h2>
          <div className="space-y-6 text-gray-600 leading-relaxed">
            <div className="border-l-4 border-[#F0C419] pl-6 py-2">
              <p className="font-semibold text-gray-800 mb-2">
                I'm a Registered Nurse with 16 years of clinical experience.
              </p>
              <p>
                I've worked with private specialists for 11 years. I understand your world, your patients, and your compliance obligations under AHPRA. I'm not learning on your time—I already know the rules.
              </p>
            </div>
            
            <div className="border-l-4 border-[#F0C419] pl-6 py-2">
              <p className="font-semibold text-gray-800 mb-2">
                Using AI tools combined with 11 years of social media experience...
              </p>
              <p>
                I create content that sounds like you, builds trust with referred patients before they ever call your rooms, and stays 100% AHPRA compliant.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg my-8">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-[#F0C419] font-bold mr-3 text-xl">✗</span>
                  <span>No cookie-cutter templates.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#F0C419] font-bold mr-3 text-xl">✗</span>
                  <span>No generic medical stock photos.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#F0C419] font-bold mr-3 text-xl">✗</span>
                  <span>No content that makes you look like every other clinic on Instagram.</span>
                </li>
              </ul>
            </div>

            <p className="text-lg font-medium text-gray-800">
              Just consistent, professional content that converts GP referrals and Google searches into booked patients while you focus on your craft.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="bg-[#F0C419] py-16 md:py-20">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <blockquote className="text-lg md:text-xl text-black leading-relaxed mb-6">
            I can highly recommend Ainsley Kerswill (Get Foxy Social Media) for anyone looking for a social media manager. She works hard to understand her client's needs and to tailor a social media strategy to best suit the client and reach the target audience. Ainsley is not only very professional in her approach but also a pleasure to work with!
          </blockquote>
          <p className="text-black font-medium">
            Dr Bryan Kenny
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
            If your online presence doesn't reflect the quality of care you actually deliver, let's fix that.
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            In 20 minutes I'll take a look at your current online presence, tell you exactly what's working and what's losing you referred patients, and give you one thing you could change today.
          </p>
          <p className="text-gray-600 mb-8">
            If we're a good fit, I'll tell you. If we're not, I'll tell you that too.
          </p>
          <a href="https://calendly.com/ainsleygreenslade/onlinepresenceaudit" target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#F0C419] hover:bg-[#D4AF37] text-black font-medium px-8 py-3 rounded transition-colors duration-200 text-lg">
              Book Your Free 20-Minute Audit
            </Button>
          </a>
        </div>
      </section>

      {/* Webador Banner */}
      <section className="bg-[#F0C419] py-4">
        <div className="container mx-auto px-6 text-center">
          <p className="text-black font-medium">
            Create Your Own Website With <span className="font-bold">webador</span>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;